import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isImpersonating, setIsImpersonating] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      const userData = JSON.parse(savedUser);
      setUser(userData);
      setIsImpersonating(!!userData.impersonating);
      
      // Verify token is still valid
      authAPI.getMe()
        .then(res => {
          setUser(res.data);
          setIsImpersonating(!!res.data.impersonating);
          localStorage.setItem('user', JSON.stringify(res.data));
        })
        .catch(() => {
          logout();
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await authAPI.login({ email, password });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      setIsImpersonating(!!userData.impersonating);
      
      // Return must_change_password flag for handling by calling component
      return { 
        success: true, 
        must_change_password: userData.must_change_password || false 
      };
    } catch (err) {
      const message = err.response?.data?.detail || 'Erreur de connexion';
      setError(message);
      return { success: false, error: message };
    }
  };

  const register = async (data) => {
    try {
      setError(null);
      const response = await authAPI.register(data);
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      setIsImpersonating(false);
      
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Erreur d\'inscription';
      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('original_admin_token');
    setUser(null);
    setIsImpersonating(false);
  };

  const stopImpersonation = async () => {
    try {
      const originalToken = localStorage.getItem('original_admin_token');
      if (originalToken) {
        // Use the original admin token to get admin user back
        localStorage.setItem('token', originalToken);
        const response = await authAPI.getMe();
        const userData = response.data;
        
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.removeItem('original_admin_token');
        setUser(userData);
        setIsImpersonating(false);
        
        return { success: true };
      }
      return { success: false, error: 'No original token found' };
    } catch (err) {
      return { success: false, error: 'Error stopping impersonation' };
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    isAuthenticated: !!user,
    isSuperAdmin: user?.is_super_admin || false,
    isAdmin: user?.is_admin || user?.is_super_admin || false,
    isCollaborator: user?.role === 'collaborator',
    isImpersonating,
    stopImpersonation
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
